import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

/**
 * Render service for the import data preview: used by both ImportWizardComponent and CalculationModelRegisterComponent
 */
@Injectable({
  providedIn: 'root'
})
export class ImportPreviewRenderService {
  /**
   * Constructor
   * @param translate TranslateService
   */
  constructor(private translate: TranslateService) { }

  //#region preview formatting

  /**
   * Gets the import title for the import area
   * @param importKey Import area translation wrapper object
   */
  getPreviewImportTitle(importKey) {
    const translationKey = `${importKey}.title`;
    const translation = this.translate.instant(translationKey);
    return translation != translationKey ? translation : importKey;
  }

  /**
   * Gets the column title
   * @param importKey Import area translation wrapper object
   * @param columnKey Column translation key: importKey.columnKey
   * @param currencySymbol Currency symbol
   */
  getPreviewColumnTitle(importKey, columnKey) {
    if (columnKey === 'TargetPropertyId') {
      importKey = 'import';
    }

    const translationKey = `${importKey}.${columnKey}`;
    const translation = this.translate.instant(translationKey);
    const translateValue = translation != translationKey ? translation : columnKey;

    const currencyKey = 'common.currency';
    const areaKey = 'common.area';

    switch (columnKey) {
      case "ManagementCosts":
      case "MaintenanceCosts":
      case "InsuranceCosts":
      case "LeaseholdCosts": {
        const yearKey = `${importKey}.Year`;
        return `${translateValue} ${this.translate.instant(currencyKey)}/${this.translate.instant(yearKey)}`;
      }
      case "TenantTenantImprovementsPerSqm": {
        return `${translateValue} ${this.translate.instant(currencyKey)}/${this.translate.instant(areaKey)}`;
      }
      default: {
        return translateValue;
      }
    }
  }

  /**
   * Gets the preview column width
   * @param _importKey Import area translation wrapper object
   * @param columnKey Column translation key
   */
  getPreviewColumnWidth(_importKey, columnKey) {
    switch (columnKey) {
      case 'ReferenceBoId':
        return 150;
      case 'TargetPropertyId':
        return 400;
      case "PropertyName":
        return 500;
      case "LeaseName":
      case "Capitalisation Group":
        return 300;
      default:
        return 200;
    }
  }

  /**
   * Checks if specific fields should be hidden
   * @param importKey Import area translation wrapper object
   * @param columnKey Column translation key
   */
  shouldShowField(importKey, columnKey) {
    switch (columnKey) {
      case 'oid':
      case 'UserId':
      case 'BuId':
      case 'ImportGuid':
      case 'CurrencyDescription':
      case 'CurrencyCode':
      case 'Month':
        {
          return false;
        }
      case 'PropertyName': {
        switch (importKey) {
          case 'OneReportTenantLevelImport':
          case 'OneReportPropertyLevelCashFlowImport':
          case 'OneReportPropertyLevelCashFlowImportMonthly':
          case 'OneReportPropertyLevelMlaImport':
          case 'OneReportTenancyScheduleLevelImport':
            {
              return false;
            }
        }

        return true;
      }
    }
    return true;
  }

  /**
   * Gets special column specific formattings
   * @param _importKey Import area translation wrapper object
   * @param columnKey Column translation key
   */
  getFormat(_importKey, columnKey) {
    switch (columnKey) {
      case 'PercentEquity':
      case 'PropertyIrr':
      case 'EquivalentYield':
      case 'TrueEquivalentYield':
      case 'InitialYield':
      case 'ReversionaryYield':
      case 'ErvRatePerSqft':
      case 'RentRatePerSqft':
      case 'PercentErv':
      case 'PercentRent':
      case 'PercentFroth':
      case 'PercentErvUpliftPerTotalRent':
      case 'VoidExpiryByBreak':
      case 'PercentErvVacancyLoss':
      case 'HardcoreYield':
      case 'ReversionYield':
      case 'FrothYield':
      case 'DcfExitYld':
      case 'NetInitialRunningYield':
        {
          return { 0: 'n2' };
        }
      case 'TotalArea':
      case 'GrossValue':
      case 'CapitalCosts':
      case 'AcquisitionCosts':
      case 'NetValue':
      case 'SayValue':
      case 'GrossRent':
      case 'GrossRentalValue':
      case 'DeemedRent':
      case 'GroundRent':
      case 'CostDeductions':
      case 'RentalValue':
      case 'AreaSqft':
      case 'Froth':
      case 'ErvUplift':
      case 'PeriodToExpiry':
      case 'PeriodToBreak':
      case 'PeriodToTermination':
      case 'VacantPeriod':
      case 'ErvVacancyLoss':
      case 'ErvVoidLoss':
      case 'NetRent':
      case 'RentFreePeriod':
      case 'ContractedRent':
      case 'BaseRentalValue':
      case 'CapitalExpenditures':
      case 'CashflowBeforeDebtService':
      case 'DisposalFeesExit':
      case 'GrossMarketIncome':
      case 'NetCashflow':
      case 'NetOperatingIncome':
      case 'OperatingExpenses':
      case 'PotentialGrossRevenue':
      case 'TerminalValue':
      case 'TotalLeasingCapitalCosts':
      case 'TotalOperatingExpenses':
      case 'TotalPotentialGrossRent':
        {
          return { 0: 'n0' }
        }
      case 'LeaseStart':
      case 'NextReview':
      case 'LeaseExpiry':
      case 'EarliestTermination': {
        return { date: 'short' };
      }
    }
    return null;
  }

  //#endregion
}
